import { useCallback, useEffect, useRef } from 'react';

import { verifyToken } from '../../../../redux/auth/thunks';
import { isAwaitedSegment } from '../../../../redux/comment_attachments/draft_comment';
import type { Permission } from '../../../../redux/entities/normalized/search_item';
import { getApprovalId } from '../../../../redux/entities/schemas';
import { updateEntities } from '../../../../redux/entities/slice';
import {
  getEntityById,
  normalizeEntities,
} from '../../../../redux/entities/utils';
import { sessionRequests } from '../../../../redux/middlewares/http/http_middleware';
import { useAppDispatch, useAppSelector } from '../../../../redux/redux_hooks';
import {
  addUserToShare,
  deleteUserFromShare,
} from '../../../../redux/share/thunks';
import { useLatest } from '../../../hooks/use_latest';
import { type Options, WebsocketManager } from './websocket_manager';
import type {
  WsAssetApprovalStatus,
  WsAssetEntity,
  WsAssetFiles,
  WsAssetFormats,
  WsAssetJobs,
  WsAssetMetadata,
  WsAssetSegments,
  WsAssetSegmentsMetadata,
  WsAssetSubtitles,
  WsAssetVersions,
  WsCollectionContents,
  WsJobEntity,
  WsNotification,
  WsPortalEntity,
} from './websocket_notification_types';

export type WebsocketManagerHookOptions = Omit<
  Options,
  'verifyToken' | 'onMessage' | 'getWebsocketUrl'
> & {
  getWebsocketUrl: () => string | undefined;
  // Object ID to subscribe to.
  // For example, to fetch comment updates this would be a specific asset ID.
  objectId?: string;
  shareId?: string;
  shareObjectType?: string | null;
  isInternalUser?: boolean;
  onCommentsUpdate?: (ids: string[]) => void;
  onApprovalsUpdate?: (ids: string[], operation: 'delete' | 'update') => void;
  onCollectionContentsMessage?: (message: WsCollectionContents) => void;
  onAssetJobsMessage?: (message: WsAssetJobs) => void;
  onJobEntityMessage?: (message: WsJobEntity) => void;
  onAssetSegmentsMessage?: (message: WsAssetSegments) => void;
  onAssetSegmentsMetadataMessage?: (message: WsAssetSegmentsMetadata) => void;
  onAssetVersionsMessage?: (message: WsAssetVersions) => void;
  onAssetFormatsMessage?: (message: WsAssetFormats) => void;
  onAssetSubtitlesMessage?: (message: WsAssetSubtitles) => void;
  onAssetMetadataMessage?: (message: WsAssetMetadata) => void;
  onAssetFilesMessage?: (message: WsAssetFiles) => void;
  onAssetEntityMessage?: (message: WsAssetEntity) => void;
  onAssetApprovalStatusMessage?: (message: WsAssetApprovalStatus) => void;
  onPortalEntityMessage?: (message: WsPortalEntity) => void;
};

const ALLOWED_OPERATIONS = new Set(['create', 'update', 'delete']);

const switchedDomain = Boolean(localStorage.getItem('domainId'));

export const useWebsocketManager = (options: WebsocketManagerHookOptions) => {
  const managerRef = useRef<WebsocketManager>(undefined);
  const dispatch = useAppDispatch();

  const verify = useCallback(() => dispatch(verifyToken()), []);
  const onCommentsUpdateRef = useLatest(options.onCommentsUpdate);
  const onApprovalsUpdateRef = useLatest(options.onApprovalsUpdate);
  const onCollectionContentsMessageRef = useLatest(
    options.onCollectionContentsMessage,
  );
  const onAssetJobsMessageRef = useLatest(options.onAssetJobsMessage);
  const onJobEntityMessageRef = useLatest(options.onJobEntityMessage);
  const onAssetSegmentsMessageRef = useLatest(options.onAssetSegmentsMessage);
  const onAssetSegmentsMetadataMessageRef = useLatest(
    options.onAssetSegmentsMetadataMessage,
  );
  const onAssetVersionsMessageRef = useLatest(options.onAssetVersionsMessage);
  const onAssetFormatsMessageRef = useLatest(options.onAssetFormatsMessage);
  const onAssetSubtitlesMessageRef = useLatest(options.onAssetSubtitlesMessage);
  const onAssetMetadataMessageRef = useLatest(options.onAssetMetadataMessage);
  const onAssetFilesMessageRef = useLatest(options.onAssetFilesMessage);
  const onAssetEntityMessageRef = useLatest(options.onAssetEntityMessage);
  const onAssetApprovalStatusMessageRef = useLatest(
    options.onAssetApprovalStatusMessage,
  );
  const onPortalEntityMessageRef = useLatest(options.onPortalEntityMessage);

  const getWebsocketUrlRef = useLatest(options.getWebsocketUrl);
  const share = useAppSelector((state) =>
    getEntityById(state, 'shares', options.shareId),
  );
  // TODO Always normalize entities before updating in Redux store
  //  https://linear.app/iconik/issue/FE-3356/update-new-websocket-hook-to-normalize-incoming-data
  const messagesHandler = useCallback(
    (messages: WsNotification[]) => {
      for (const message of messages) {
        const { realm, operation, data } = message;

        if (!ALLOWED_OPERATIONS.has(operation)) {
          continue;
        }

        if (realm === 'entity' && message.object_type === 'portals') {
          if (sessionRequests.has(message.request_id)) {
            continue;
          }
          // Delete notifications carry no data, only object_id, so portals are
          // handled ahead of the shared `!data` guard below
          if (operation !== 'delete' && message.data) {
            dispatch(
              updateEntities({
                portals: { [message.data.id]: message.data },
              }),
            );
          }
          onPortalEntityMessageRef.current?.(message);
          continue;
        }

        if (!data) {
          continue;
        }

        if (
          realm === 'contents' &&
          operation === 'create' &&
          !sessionRequests.has(message.request_id)
        ) {
          onCollectionContentsMessageRef.current?.(message);
        }

        if (
          realm === 'entity' &&
          message.object_type === 'playlists' &&
          operation === 'update'
        ) {
          dispatch(
            updateEntities({
              playlists: {
                [message.data.id]: message.data,
              },
            }),
          );
        }

        if (
          realm === 'entity' &&
          message.object_type === 'assets' &&
          operation === 'update'
        ) {
          const {
            created_by_user_info,
            updated_by_user_info,
            deleted_by_user_info,
            ...dataWithoutUserInfo
          } = message.data;

          dispatch(
            updateEntities({
              assets: {
                [message.data.id]: dataWithoutUserInfo,
              },
              search_items: {
                [message.data.id]: dataWithoutUserInfo,
              },
            }),
          );
          onAssetEntityMessageRef.current?.(message);
        }

        if (
          realm === 'entity' &&
          message.object_type === 'collections' &&
          operation === 'update'
        ) {
          // Make collection type compatible with SearchItemEntity.
          const collection = {
            ...message.data,
            // biome-ignore lint/plugin: the branch above already checked the message is a collections update.
            object_type: message.data.object_type as 'collections',
            // biome-ignore lint/plugin: API boundary, the socket payload types permissions as string[].
            permissions: message.data.permissions as Permission[],
          };
          dispatch(
            updateEntities({
              collections: {
                [message.data.id]: collection,
              },
              search_items: {
                [message.data.id]: collection,
              },
            }),
          );
        }

        if (message.realm === 'entity' && message.object_type === 'jobs') {
          onJobEntityMessageRef.current?.(message);
          dispatch(
            updateEntities({ jobs: { [message.data.id]: message.data } }),
          );
        }

        if (message.realm === 'jobs') {
          dispatch(
            updateEntities({ jobs: { [message.data.id]: message.data } }),
          );
          if (message.object_type === 'assets') {
            onAssetJobsMessageRef.current?.(message);
          }
        }

        if (
          realm === 'keyframes' &&
          message.object_type === 'assets' &&
          message.data.asset_id
        ) {
          const updatedKeyframe = data;
          if (operation === 'delete') {
            updatedKeyframe.status = 'DELETED';
          }

          dispatch(
            updateEntities({
              search_items: {
                [message.data.asset_id]: { keyframes: [updatedKeyframe] },
              },
              keyframes: { [data.id]: updatedKeyframe },
            }),
          );
        }

        if (realm === 'proxies' && data.asset_id) {
          const updatedProxy = data;
          if (operation === 'delete') {
            updatedProxy.status = 'DELETED';
          }

          dispatch(
            updateEntities({
              search_items: { [data.asset_id]: { proxies: [updatedProxy.id] } },
              proxies: { [data.id]: updatedProxy },
            }),
          );
        }

        if (
          realm === 'approval_status' &&
          message.data.version_id &&
          message.data.object_id
        ) {
          const assetId = message.data.object_id;

          dispatch(
            updateEntities({
              search_items: {
                [assetId]: {
                  approval: {
                    status: message.data.status,
                  },
                },
              },
            }),
          );
          onAssetApprovalStatusMessageRef.current?.(message);
        }

        if (
          realm === 'share_users' &&
          operation === 'create' &&
          message.data.share_id
        ) {
          dispatch(addUserToShare(message.data.share_id, message.data));
        }

        if (
          realm === 'share_users' &&
          operation === 'delete' &&
          message.data.share_id
        ) {
          dispatch(deleteUserFromShare(message.data.share_id, message.data));
        }

        if (
          realm === 'approvals' &&
          message.data.version_id &&
          message.data.object_id
        ) {
          const approval = message.data;
          const id = getApprovalId(approval);

          if (operation === 'delete') {
            onApprovalsUpdateRef.current?.([id], operation);
          }
          if (operation === 'update') {
            if (approval.object_id) {
              dispatch(
                updateEntities({
                  search_items: {
                    [approval.object_id]: {
                      approval: { approvals_by: [approval] },
                    },
                  },
                }),
              );
            }
            dispatch(
              updateEntities({
                approvals: { [id]: approval },
              }),
            );
            onApprovalsUpdateRef.current?.([id], operation);
          }
        }

        if (message.realm === 'files' && message.data.asset_id) {
          const normalizedData = normalizeEntities(message.data, 'files');
          dispatch(updateEntities(normalizedData.entities));
          onAssetFilesMessageRef.current?.(message);
        }

        if (message.realm === 'versions') {
          onAssetVersionsMessageRef.current?.(message);
        }

        if (message.realm === 'formats') {
          onAssetFormatsMessageRef.current?.(message);
        }

        if (message.realm === 'subtitles') {
          onAssetSubtitlesMessageRef.current?.(message);
        }

        if (message.realm === 'metadata' && message.object_type === 'assets') {
          onAssetMetadataMessageRef.current?.(message);
        }

        if (message.realm === 'segments_metadata') {
          onAssetSegmentsMetadataMessageRef.current?.(message);
        }

        const dataWithShareId = 'share_id' in data && Boolean(data.share_id);
        // TODO: RP-2432 implement batch handling of entities update under R
        const nonShareDataMessage =
          !dataWithShareId && options.shareObjectType === 'playlists';
        const isSegmentMessage = realm === 'segments';
        const shouldSkipNonShareMessage =
          share?.show_existing_comments &&
          isSegmentMessage &&
          nonShareDataMessage
            ? false
            : nonShareDataMessage;

        if (shouldSkipNonShareMessage) {
          continue;
        }

        if (
          options.shareId &&
          dataWithShareId &&
          data.share_id !== options.shareId
        ) {
          continue;
        }
        // TODO: Move share comment logic to the backend for better maintainability and consistency.
        if (
          !options?.isInternalUser &&
          message.realm === 'segments' &&
          message.data.is_internal === true
        ) {
          continue;
        }

        if (message.realm === 'segments') {
          if (isAwaitedSegment(message.data)) {
            continue;
          }
          const isOwnRequest = sessionRequests.has(message.request_id);
          // An echo is our own when it names the segment our request targeted or a write by id names it in the
          // path params.  A create operation here is always considered to be an echo.
          // Re-merging our own echo would risk reverting a newer local update, so we suppress the merge.
          // Everything else under our request id is a backend initiated cascade, such as a visibility
          // change for the replies, and need to be merged.
          const isOwnEcho =
            isOwnRequest &&
            (operation === 'create' ||
              sessionRequests.get(message.request_id)?.segmentId ===
                message.data.id);

          if (!isOwnEcho) {
            const segmentData = {
              ...message.data,
              status: operation === 'delete' ? 'DELETED' : message.data.status,
            };
            const normalizedData = normalizeEntities(segmentData, 'segments');
            dispatch(updateEntities(normalizedData.entities));
          }

          if (!isOwnRequest) {
            onCommentsUpdateRef.current?.([message.data.id]);
            onAssetSegmentsMessageRef.current?.(message);
          }
        }
      }
    },
    [
      options.shareId,
      options.shareObjectType,
      share?.show_existing_comments,
      options?.isInternalUser,
    ],
  );

  useEffect(() => {
    if (switchedDomain) {
      return;
    }

    const websocketManager = new WebsocketManager({
      onMessage: messagesHandler,
      verifyToken: verify,
      multiListenerKey: options.multiListenerKey,
      getWebsocketUrl: getWebsocketUrlRef.current,
    });
    websocketManager.init();
    managerRef.current = websocketManager;

    return () => {
      websocketManager.destroy();
    };
  }, [verify, messagesHandler, options.multiListenerKey]);

  useEffect(() => {
    if (!options.objectId) {
      return;
    }
    managerRef.current?.subscribe(options.objectId);

    return () => {
      if (!options.objectId) {
        return;
      }

      managerRef.current?.unsubscribe(options.objectId);
    };
  }, [options.objectId]);
};
